import React, { useState } from "react";
import { X, Check, Loader2, ArrowRight } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const INTERESTS = [
    "General",
    "Legal AI",
    "Government Affairs",
    "Enterprise plan",
    "Enterprise inquiry",
    "Enterprise Plus",
    "Business plan",
    "Professional plan",
    "Healthcare Admin",
    "Legal",
    "Custom vertical",
];

/**
 * BookDemoModal
 * Public demo-booking form. Submits to /api/demo-requests (no auth).
 * Honeypot: hidden `website` field — bots often fill it, real users leave blank.
 * Prefill: optional `defaultInterest` prop (e.g., pass "Enterprise plan" from
 * a plan card's "Contact sales" button).
 */
export default function BookDemoModal({ open, onClose, defaultInterest }) {
    const [state, setState] = useState({
        full_name: "",
        work_email: "",
        company_name: "",
        company_size: "",
        role: "",
        interest: defaultInterest || "",
        preferred_time: "",
        message: "",
        website: "", // honeypot
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    React.useEffect(() => {
        // Always sync interest to the caller's defaultInterest when the modal opens.
        // Previously we only synced when state.interest was empty, which lost prefill
        // when reopening the modal from a different CTA.
        if (open) {
            setState((s) => ({ ...s, interest: defaultInterest || s.interest || "" }));
        }
        // eslint-disable-next-line
    }, [open, defaultInterest]);

    if (!open) return null;

    const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!state.full_name.trim() || !state.work_email.trim() || !state.company_name.trim()) {
            setError("Please fill in name, work email, and company.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API_BASE}/demo-requests`, {
                ...state,
                source_page: typeof window !== "undefined" ? window.location.pathname : "/",
                utm: parseUtm(),
            });
            setSuccess(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(detail || "Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const parseUtm = () => {
        if (typeof window === "undefined") return {};
        const p = new URLSearchParams(window.location.search);
        const out = {};
        for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
            const v = p.get(k);
            if (v) out[k] = v;
        }
        return out;
    };

    const close = () => {
        onClose?.();
        // reset after close animation
        setTimeout(() => {
            setSuccess(false);
            setError(null);
            setState({
                full_name: "", work_email: "", company_name: "",
                company_size: "", role: "", interest: defaultInterest || "",
                preferred_time: "", message: "", website: "",
            });
        }, 200);
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 grid place-items-center z-[100] p-4"
            data-testid="book-demo-modal"
            onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
            <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[90vh] overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b border-[#e8e8e3] flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#16a34a]">Book a demo</div>
                        <div className="text-lg font-semibold text-gray-900 mt-0.5">Meet your AI workforce.</div>
                    </div>
                    <button
                        onClick={close}
                        data-testid="book-demo-close"
                        className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-[#f4f4ef]"
                    >
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>

                {/* Body */}
                {success ? (
                    <div className="p-8 text-center">
                        <div className="w-14 h-14 mx-auto rounded-full bg-[#dcfce7] grid place-items-center">
                            <Check className="w-6 h-6 text-[#16a34a]" strokeWidth={2.5} />
                        </div>
                        <div className="mt-4 text-xl font-semibold text-gray-900" data-testid="book-demo-success">Thanks for reaching out.</div>
                        <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                            Our team will reach out within <b>1 business day</b> to schedule your walkthrough.
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                            <a
                                href="/signup"
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d]"
                            >
                                Explore while you wait <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                            </a>
                            <button
                                onClick={close}
                                className="inline-flex items-center justify-center gap-1 rounded-lg border border-[#e8e8e3] bg-white px-4 h-10 text-sm text-gray-700 hover:border-[#d6d6cf]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="overflow-y-auto max-h-[70vh]">
                        <div className="px-6 py-4 space-y-3">
                            {/* Honeypot — visually hidden, real users leave blank */}
                            <div className="hidden" aria-hidden="true">
                                <label htmlFor="bd-hp-website">Website (leave blank)</label>
                                <input
                                    id="bd-hp-website"
                                    name="website"
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={state.website}
                                    onChange={(e) => set("website", e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Full name *" testid="bd-full-name">
                                    <input required value={state.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
                                </Field>
                                <Field label="Work email *" testid="bd-work-email">
                                    <input required type="email" value={state.work_email} onChange={(e) => set("work_email", e.target.value)} className={inputCls} />
                                </Field>
                            </div>
                            <Field label="Company *" testid="bd-company">
                                <input required value={state.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} />
                            </Field>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Company size" testid="bd-size">
                                    <select value={state.company_size} onChange={(e) => set("company_size", e.target.value)} className={inputCls}>
                                        <option value="">Select…</option>
                                        {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </Field>
                                <Field label="Your role" testid="bd-role">
                                    <input value={state.role} onChange={(e) => set("role", e.target.value)} placeholder="CIO, GC, Head of Ops…" className={inputCls} />
                                </Field>
                            </div>
                            <Field label="What interests you?" testid="bd-interest">
                                <select value={state.interest} onChange={(e) => set("interest", e.target.value)} className={inputCls}>
                                    <option value="">General</option>
                                    {[...new Set([...(state.interest && !INTERESTS.includes(state.interest) ? [state.interest] : []), ...INTERESTS])].map((i) => <option key={i} value={i}>{i}</option>)}
                                </select>
                            </Field>
                            <Field label="Preferred time (optional)" testid="bd-time">
                                <input value={state.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} placeholder="Next week Tues afternoon EST" className={inputCls} />
                            </Field>
                            <Field label="Message / use case" testid="bd-message">
                                <textarea rows={3} value={state.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us what you're trying to solve." className={`${inputCls} resize-none`} />
                            </Field>

                            {error && (
                                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="book-demo-error">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-[#e8e8e3] bg-[#fafaf7] flex items-center justify-between gap-3">
                            <div className="text-[11px] text-gray-500">
                                No spam. No credit card. 1-business-day response.
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="book-demo-submit"
                                className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d] disabled:opacity-50"
                            >
                                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> Sending…</> : <>Request demo <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></>}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

const inputCls =
    "w-full h-9 rounded-md border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20";

function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="wfos-label mb-1 block">{label}</span>
            {children}
        </label>
    );
}
